/* Menu page: diet filters, the match-mode toggle, and the live counts.
 *
 * THE FILTER READS THE RENDERED PILLS, NOT THE PRODUCT'S TAGS.
 *
 * snippets/menu-diet-tags.liquid applies a precedence rule after its tag loop:
 * a `contains-` claim outranks the matching free-from claim, so a dish tagged
 * both contains-nuts and nut-free renders "Contains nuts" and NO NF pill. A
 * filter reading tags would not know that, and "Nut-free" would return a dish
 * whose own card says it contains nuts — a filter contradicting its own label,
 * on a food-safety surface.
 *
 * Reading the pills the card already rendered makes that impossible: there is
 * no `.nutfree` element to match. One copy of the allergen logic, which is the
 * rule menu-diet-tags.liquid exists to state.
 *
 * NO-JS: every dish is in the HTML and visible. This file only ever hides
 * things. Since 2026-10-01 the filter bar and its buttons are in the HTML
 * from the start, so the menu does not shift. With JavaScript off, a
 * `<noscript>` style in the section hides the bar. The page is then the
 * whole menu, which is the honest fallback.
 */
(function () {
  'use strict';

  /* Label per pill class. Only classes that actually rendered become filters,
   * so an option that could match nothing is never offered. The order is the
   * design's (FILTERS in menu-page.jsx), not alphabetical. */
  var KNOWN = [
    ['gf', 'Gluten-free'],
    ['df', 'Dairy-free'],
    ['veg', 'Vegetarian'],
    ['vegan', 'Vegan'],
    /* With the other diets, for the reason HP gives below. Added 2026-09-24:
     * DEV_BRIEF_2026-08-30 §P3a confined Pescatarian to the filter bar, and
     * nobody built the filter. */
    ['pesc', 'Pescatarian'],
    /* ⚠️ HP SITS WITH THE DIETS HERE, AND LAST ON THE CARD. That is a deliberate
     * difference, not an oversight, and it is worth the two lines to say why.
     *
     * On a CARD the marks are claims about one dish, and the drop puts HP last
     * (§6) so an aspiration is not read as one more allergen fact in the run
     * beside it. On the FILTER BAR they are controls, and Heidi's call on
     * 2026-09-01 is that HP belongs with Vegetarian and Vegan — the other two
     * filter::diet:: tags — rather than trailing the free-from filters.
     *
     * This diverges from the drop's §5.4, which lists HP last in the key too.
     * Recorded rather than quietly reconciled: if the key is ever built from
     * this array, it will inherit this order and not the drop's.
     *
     * A filter only appears if some dish claims it, so HP stays invisible on a
     * menu with no high-protein dishes rather than offering an empty control. */
    ['hp', 'High protein'],
    ['ef', 'Egg-free'],
    ['nutfree', 'Nut-free'],
    ['nochili', 'No chilis']
  ];

  /* ⚠️ VEGAN SATISFIES VEGETARIAN, and this function is the reason the display
   * rule beside it is safe to have.
   *
   * snippets/menu-diet-tags.liquid suppresses the V pill on any dish that is
   * also VG, so the two identical herb rings can never sit side by side. But
   * `claims` below is built from each dish's RENDERED pills — that is
   * deliberate, because menu-diet-tags clears NF on a dish tagged contains-nuts
   * and a tag-reading filter would hand that dish back under "Nut-free".
   *
   * The cost is that a suppressed pill is also an erased claim. Without this,
   * hiding V would drop every vegan dish out of the Vegetarian FILTER: 25 of 77
   * on the trial store when this was written. The drop forbids that in the same
   * breath as it asks for the suppression — "the Vegetarian filter still
   * returns vegan dishes" — because a hidden mark that also hides the food is
   * worse than a redundant mark.
   *
   * Used by `available` as well as `matches`, so the Vegetarian control still
   * appears even if every vegetarian dish happened to be vegan. */
  function holds(claim, id) {
    if (id === 'veg') return !!(claim.veg || claim.vegan);
    /* ⚠️ THE SAME RULE, ONE STEP OUT. menu-diet-tags suppresses P wherever V or
     * VG shows, so P renders only on the fish dishes (Heidi, 2026-09-24).
     * Without this line the Pescatarian filter would return those 19 dishes
     * and hide every vegetarian and vegan dish from a pescatarian, which is
     * the food they can eat most of.
     *
     * It is true by definition, so it needs no tag. That also makes it right
     * for the 23 vegetarian dishes on trial that carry no pescetarian tag. */
    if (id === 'pesc') return !!(claim.pesc || claim.veg || claim.vegan);
    /* GF OPTION, MAV-944, Heidi 2026-10-19. A dish with a gluten-free variant
     * (corn or flour tortillas) shows "GF option" and never the GF ring. The
     * Gluten-free filter still returns it, because a customer can order it
     * gluten-free. Its own pill tells them which choice to make. */
    if (id === 'gf') return !!(claim.gf || claim.gfoption);
    return !!claim[id];
  }

  function init(root) {
    var bar = root.querySelector('[data-mv-filters]');
    var pillBox = root.querySelector('[data-mv-filter-pills]');
    var modeBox = root.querySelector('[data-mv-mode]');
    var clearBtn = root.querySelector('[data-mv-clear]');
    var resultLine = root.querySelector('[data-mv-result]');
    var emptyLine = root.querySelector('[data-mv-empty]');
    /* `data-mv-search-input`, not `data-mv-search`: the dish cards carry
     * `data-mv-search` for their text, so a shared name would find whichever
     * came first on the page. */
    var searchInput = root.querySelector('[data-mv-search-input]');
    var searchNote = root.querySelector('[data-mv-search-note]');
    var dishes = [].slice.call(root.querySelectorAll('[data-mv-dish]'));
    /* The bar is in the HTML from the start (2026-10-01, see the comment
     * above it in sections/maven-menu.liquid). A menu with no dishes offers
     * no filter, so it hides the bar here. */
    if (bar && (!pillBox || !dishes.length)) bar.hidden = true;
    if (!bar || !pillBox || !dishes.length) return;

    /* ── The search (Heidi, 2026-09-26) ──────────────────────────────────
     *
     * Each card carries `data-mv-search`: its name and Kumquat's ingredient
     * list, lowercased (snippets/maven-dish-card.liquid). A search matches a
     * dish when EVERY word typed appears in that text, so "dark chocolate"
     * finds a dish with both words, anywhere. Accents are folded, so "creme"
     * finds "crème". It combines with the diet filters: a dish shows only if
     * it passes both.
     *
     * ⚠️ IT IS A WORD SEARCH, NOT AN ALLERGEN CHECK, AND IT SAYS SO. Kumquat's
     * ingredient text is written freely, so "peanut" can miss "groundnut". An
     * empty result is exactly when somebody might read "nothing here contains
     * it", so that is when the note appears. Heidi chose that wording on
     * 2026-09-26. */
    function fold(t) {
      t = String(t || '').toLowerCase();
      return t.normalize ? t.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : t;
    }
    var hay = dishes.map(function (d) { return fold(d.getAttribute('data-mv-search')); });
    var term = '';
    var words = [];
    function searchOk(i) {
      return words.every(function (w) { return hay[i].indexOf(w) !== -1; });
    }

    /* ── Everything orderable now (Heidi, 2026-09-26) ─────────────────────
     *
     * A search also reads the other menu page: This Week's from Everyday,
     * Everyday from This Week's, and both from a collection page. The pages
     * are named in `data-mv-search-also` on the section. Each is fetched once,
     * on the first search, and its cards are kept but not shown until they
     * match. Those cards were windowed by their own section, so only
     * orderable dishes arrive. A dish already on this page is dropped, so no
     * dish shows twice.
     *
     * The copies have no Add button, because the tray script adds those at
     * load. `maven:dishes-added` asks it to add them to the new cards. */
    var alsoBox = root.querySelector('[data-mv-search-also]');
    var alsoSources = [];
    try { alsoSources = JSON.parse(alsoBox ? alsoBox.getAttribute('data-mv-search-also') : '[]') || []; } catch (e) { alsoSources = []; }
    function hrefOf(d) {
      var a = d.querySelector('a[href*="/products/"]');
      return a ? a.getAttribute('href').replace(/[?#].*$/, '') : '';
    }
    var seenHrefs = {};
    dishes.forEach(function (d) { var h = hrefOf(d); if (h) seenHrefs[h] = true; });
    var also = null;      /* [{label, cards:[{el, hay, claims}]}] once loaded */
    var alsoLoading = null;
    function loadAlso() {
      if (also || alsoLoading || !alsoSources.length || !window.fetch) return alsoLoading;
      alsoLoading = Promise.all(alsoSources.map(function (src) {
        return fetch(src.url, { headers: { Accept: 'text/html' } })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .catch(function () { return ''; })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var cards = [];
            [].forEach.call(doc.querySelectorAll('[data-mv-dish]'), function (d) {
              var h = hrefOf(d);
              if (!h || seenHrefs[h]) return;
              seenHrefs[h] = true;
              var el = document.importNode(d, true);
              el.removeAttribute('hidden');
              cards.push({ el: el, hay: fold(el.getAttribute('data-mv-search')), claims: claimsOf(el) });
            });
            return { label: src.label, cards: cards };
          });
      })).then(function (groups) { also = groups; alsoLoading = null; apply(); });
      return alsoLoading;
    }
    /* The matching copies, shown under their label. Returns how many. */
    function renderAlso() {
      if (!alsoBox) return 0;
      alsoBox.textContent = '';
      if (!term || !also) { alsoBox.hidden = true; return 0; }
      var total = 0;
      var added = [];
      also.forEach(function (g) {
        var hits = g.cards.filter(function (c) {
          return matches(c.claims) && words.every(function (w) { return c.hay.indexOf(w) !== -1; });
        });
        if (!hits.length) return;
        total += hits.length;
        var h = document.createElement('h2');
        h.className = 'mv-meal__name mv-menu-also-results__label';
        h.textContent = g.label;
        var ul = document.createElement('ul');
        ul.className = 'mv-dishes';
        ul.setAttribute('role', 'list');
        hits.forEach(function (c) { ul.appendChild(c.el); added.push(c.el); });
        alsoBox.appendChild(h);
        alsoBox.appendChild(ul);
      });
      alsoBox.hidden = total === 0;
      if (added.length) {
        document.dispatchEvent(new CustomEvent('maven:dishes-added', { detail: { cards: added } }));
      }
      return total;
    }

    /* What each dish claims, read off its own pills. */
    function claimsOf(d) {
      var set = {};
      [].forEach.call(d.querySelectorAll('.tag'), function (t) {
        [].forEach.call(t.classList, function (c) { set[c] = true; });
      });
      /* HELD CLAIMS — true of the dish, deliberately not shown as a pill.
       *
       * Reading rendered pills is what keeps a dish tagged contains-nuts out of
       * the Nut-free filter: menu-diet-tags clears the flag and the pill goes
       * with it. But nut-free itself renders no pill at all now (drop §1), and
       * the filter is the ONLY place that claim is made — so without this it
       * would vanish from its own control.
       *
       * Class names, so the two vocabularies stay one. */
      [].forEach.call(d.querySelectorAll('[data-mv-claims]'), function (el) {
        (el.getAttribute('data-mv-claims') || '').split(/\s+/).forEach(function (c) {
          if (c) set[c] = true;
        });
      });
      return set;
    }
    var claims = dishes.map(claimsOf);

    var available = KNOWN.filter(function (k) {
      return claims.some(function (c) { return holds(c, k[0]); });
    });
    /* A page with no diet pill to offer still gets the search. */
    if (!available.length && !searchInput) { bar.hidden = true; return; }
    if (!available.length) {
      [].forEach.call(bar.querySelectorAll('.mv-menu-filters__label, [data-mv-filter-pills], [data-mv-mode]'),
        function (el) { el.hidden = true; });
    }

    var active = [];
    var mode = 'all';

    /* The buttons are rendered in the HTML, one per KNOWN key. Remove each
     * one no dish on this page claims, and use the rest. A key the HTML does
     * not carry is still created, as before. */
    var rendered = {};
    [].forEach.call(pillBox.querySelectorAll('[data-mv-filter-key]'), function (el) {
      rendered[el.getAttribute('data-mv-filter-key')] = el;
    });
    var offered = available.map(function (k) { return k[0]; });
    Object.keys(rendered).forEach(function (key) {
      if (offered.indexOf(key) === -1) rendered[key].parentNode.removeChild(rendered[key]);
    });

    available.forEach(function (k) {
      var b = rendered[k[0]];
      var isNew = !b;
      if (isNew) {
        b = document.createElement('button');
        b.type = 'button';
        b.className = 'mv-menu-filter tag abbr ' + k[0];
        b.textContent = k[1];
        b.setAttribute('aria-pressed', 'false');
      }
      b.addEventListener('click', function () {
        var i = active.indexOf(k[0]);
        if (i === -1) { active.push(k[0]); } else { active.splice(i, 1); }
        b.setAttribute('aria-pressed', i === -1 ? 'true' : 'false');
        b.classList.toggle('is-on', i === -1);
        apply();
      });
      if (isNew) pillBox.appendChild(b);
    });

    if (modeBox) {
      [].forEach.call(modeBox.querySelectorAll('[data-mv-mode-opt]'), function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          mode = btn.getAttribute('data-mv-mode-opt');
          [].forEach.call(modeBox.querySelectorAll('[data-mv-mode-opt]'), function (o) {
            o.classList.toggle('is-on', o === btn);
            /* MAV-638: the colour alone does not tell a screen reader which
             * mode is on. The filter pills above carry aria-pressed too. */
            o.setAttribute('aria-pressed', o === btn ? 'true' : 'false');
          });
          apply();
        });
      });
    }

    function clear() {
      active = [];
      term = '';
      words = [];
      if (searchInput) searchInput.value = '';
      [].forEach.call(pillBox.querySelectorAll('.mv-menu-filter'), function (b) {
        b.classList.remove('is-on');
        b.setAttribute('aria-pressed', 'false');
      });
      apply();
    }
    if (clearBtn) clearBtn.addEventListener('click', clear);

    if (searchInput) {
      var searchTimer = null;
      searchInput.addEventListener('input', function () {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          term = searchInput.value.replace(/\s+/g, ' ').trim();
          words = fold(term).split(' ').filter(Boolean);
          if (term) loadAlso();
          apply();
        }, 150);
      });
      /* Escape clears the search and nothing else. */
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchInput.value) {
          searchInput.value = '';
          term = '';
          words = [];
          apply();
        }
      });
    }
    var clear2 = root.querySelector('[data-mv-clear-2]');
    if (clear2) clear2.addEventListener('click', clear);

    function matches(c) {
      if (!active.length) return true;
      return mode === 'all'
        ? active.every(function (id) { return holds(c, id); })
        : active.some(function (id) { return holds(c, id); });
    }

    function apply() {
      var shown = 0;
      dishes.forEach(function (d, i) {
        var ok = matches(claims[i]) && searchOk(i);
        d.hidden = !ok;
        if (ok) shown++;
      });

      /* A meal whose dishes are all filtered out hides its heading too — a
       * heading over nothing reads as a section that failed to load. Same
       * reason the section refuses to render an empty menu at all.
       *
       * ⚠️ EXCEPT THE MENU THAT IS SAYING WHY IT HAS NOTHING. A pending menu
       * carries a heading and a note and ZERO dish elements by design, so this
       * rule read it as filtered-to-empty and hid it on page load — before a
       * filter was ever touched, because apply() runs once at init. The note
       * survived only with JavaScript off, which is the one case it was not
       * written for, and `#mt-dinner` went back to resolving to nothing.
       *
       * Caught in review, not by us: the served HTML was correct and the bug
       * only existed after the script ran. READ THE RENDERED PAGE, not the
       * response, when the thing you changed can be undone by JavaScript. */
      [].forEach.call(root.querySelectorAll('[data-mv-meal]'), function (meal) {
        if (meal.hasAttribute('data-mv-meal-pending')) return;
        var live = meal.querySelectorAll('[data-mv-dish]:not([hidden])').length;
        meal.hidden = live === 0;
        [].forEach.call(meal.querySelectorAll('[data-mv-cat]'), function (cat) {
          var catLive = cat.querySelectorAll('[data-mv-dish]:not([hidden])').length;
          cat.hidden = catLive === 0;
          /* The per-category count is a statement about what is on screen, so
           * it has to follow the filter. Left at its server-rendered value it
           * would read "7 dishes" over three. */
          var cc = cat.querySelector('[data-mv-cat-count]');
          if (cc) cc.textContent = catLive + (catLive === 1 ? ' dish' : ' dishes');
        });
      });
      document.dispatchEvent(new CustomEvent('maven:menu-filtered'));

      if (clearBtn) clearBtn.hidden = active.length === 0 && !term;
      /* §6.2: disabled below two filters, NOT hidden. A control that vanishes
       * reads as a bug; one that greys out explains itself.
       *
       * The state goes on the BUTTONS, never on the wrapper (30 Aug §7.1).
       * On the wrapper it also dims the MATCH label, which is not a control
       * and therefore gets no WCAG exemption for being dim. `aria-disabled`
       * alongside the property is what the brief asks for, so the state is
       * announced whichever way the control is reached. */
      if (modeBox) {
        var off = active.length < 2;
        [].forEach.call(modeBox.querySelectorAll('[data-mv-mode-opt]'), function (o) {
          o.disabled = off;
          o.setAttribute('aria-disabled', off ? 'true' : 'false');
          /* Greying says the control is off; it does not say why, and "why" is
           * the question somebody who just clicked it twice is asking. A title
           * is a hint and not the only cue — the disabled styling carries that,
           * and `aria-disabled` carries it to a screen reader. */
          if (off) o.title = 'Pick two or more filters to choose how they combine';
          else o.removeAttribute('title');
        });
      }
      var alsoShown = renderAlso();
      /* While the other menu is still loading, an empty page is not yet an
       * empty search, so the note waits for it. */
      var pendingAlso = !!(term && alsoLoading);

      /* With a search, the note explains an empty result. Without one, the
       * filters' own empty line does. */
      if (emptyLine) emptyLine.hidden = shown !== 0 || !!term;
      if (searchNote) {
        searchNote.textContent = '';
        searchNote.hidden = !(term && shown === 0 && alsoShown === 0 && !pendingAlso);
        if (!searchNote.hidden) {
          searchNote.appendChild(document.createTextNode(
            'No dish on this page lists \u201c' + term + '\u201d. This is a word search, not an allergen check. Read each dish\u2019s '));
          var link = document.createElement('a');
          link.href = '/pages/ingredients';
          link.textContent = 'full ingredient list';
          searchNote.appendChild(link);
          searchNote.appendChild(document.createTextNode(' before you order.'));
        }
      }
      if (resultLine) {
        if (term && active.length) {
          resultLine.textContent = 'Showing ' + shown + ' of ' + dishes.length + ' dishes for \u201c' + term + '\u201d';
        } else if (term) {
          var all = shown + alsoShown;
          resultLine.textContent = all + (all === 1 ? ' dish mentions ' : ' dishes mention ') + '\u201c' + term + '\u201d' +
            (alsoShown && shown ? ' (' + alsoShown + ' on the other menu, below)' : alsoShown ? ', on the other menu below' : '');
        } else {
          resultLine.textContent = active.length
            ? 'Showing ' + shown + ' of ' + dishes.length + ' dishes'
            : '';
        }
      }
    }

    bar.hidden = false;
    apply();
  }

  /* ── The Jump-to control (handoff README §8) ────────────────────────────
   *
   * THE LIST IS BUILT FROM THE RENDERED PAGE. The menus live in maven-menu's
   * blocks and this is a different section; Liquid gives one section no way to
   * read another's. Reading the DOM means the jump list cannot offer a menu
   * that did not render — a menu with no live products is invisible by design,
   * and a link to it would scroll to nothing.
   *
   * It also means the list follows the diet filter: filtering Dinner down to
   * nothing removes Dinner from the list rather than leaving a dead entry.
   */
  function initJump() {
    var fab = document.querySelector('[data-mv-jump]');
    if (!fab) return;
    /* Same reason as boot(). The list rebuilds from scratch so it never
     * doubled visibly, but a second pass added a second scroll listener, a
     * second Escape handler and a second maven:menu-filtered subscriber —
     * every scroll frame doing the scroll-spy twice. */
    if (fab.getAttribute('data-mv-jump-ready')) return;
    fab.setAttribute('data-mv-jump-ready', '1');
    var btn = fab.querySelector('[data-mv-jump-btn]');
    var pop = fab.querySelector('[data-mv-jump-pop]');
    var list = fab.querySelector('[data-mv-jump-list]');
    if (!btn || !pop || !list) return;

    function meals() {
      return [].filter.call(
        document.querySelectorAll('[data-mv-meal]'),
        function (m) { return !m.hidden && m.id; }
      );
    }

    /* THE OTHER PAGE'S MENUS, fetched rather than declared.
     *
     * Heidi asked for every menu in one list and for the This Week / Everyday
     * switch to come out of the panel. The obvious way is a setting naming the
     * other page's menus, and it is also the way that breaks: a menu with no
     * live products does not render, so a hardcoded list would advertise it and
     * link to an anchor that is not on the page.
     *
     * Fetching keeps the guarantee the panel already had. The other page is read
     * with the SAME selectors as this one, through the SAME renderer, so the
     * list still cannot claim a menu that did not render — on either page.
     *
     * Lazy, once, and cached: nothing is fetched until the panel is first
     * opened, so the cost lands on the small number of people who use it rather
     * than on every page load.
     *
     * On any failure it renders nothing and says nothing. A slightly shorter
     * list is a far better outcome than a list of dead anchors, and there is no
     * useful action for a customer in "could not load the other menu". */
    /* A COLLECTION PAGE HAS NO MENUS OF ITS OWN (Heidi, 2026-10-28). The
     * section marks it `data-mv-jump-standalone` and names both menu pages,
     * so the panel lists the menus of both, fetched by the same code below.
     * Until a fetch lands, or if it fails, that page shows as one plain link,
     * so the panel is never empty and never lists a dead anchor. */
    var standalone = fab.hasAttribute('data-mv-jump-standalone');

    function source(url, name) {
      var items = null;      /* null = not fetched, [] = fetched and empty */
      var pending = false;

      function render() {
        if (!url || !name) return;
        if (!items || !items.length) {
          if (!standalone) return;
          var li = document.createElement('li');
          li.className = 'mv-jump__meal';
          var a = document.createElement('a');
          a.href = url;
          a.textContent = name;
          li.appendChild(a);
          list.appendChild(li);
          return;
        }
        var head = document.createElement('li');
        head.className = 'mv-jump__group';
        head.setAttribute('role', 'presentation');
        head.textContent = name;
        list.appendChild(head);
        items.forEach(function (m) { list.appendChild(mealItem(m, url)); });
      }

      function load(then) {
        if (items || pending || !url || !name || !window.fetch) return;
        pending = true;
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            items = [].filter.call(
              doc.querySelectorAll('[data-mv-meal]'),
              function (m) { return !m.hidden && m.id; }
            );
            pending = false;
            if (items.length) then();
          })
          .catch(function () { items = []; pending = false; });
      }

      return { render: render, load: load };
    }

    var others = [source(
      fab.getAttribute('data-mv-jump-other-url') || '',
      fab.getAttribute('data-mv-jump-other-name') || ''
    )];
    if (standalone) {
      others.push(source(
        fab.getAttribute('data-mv-jump-also-url') || '',
        fab.getAttribute('data-mv-jump-also-name') || ''
      ));
    }

    function build() {
      var ms = meals();
      /* Nothing to jump to — one menu, or none. The control removes itself
       * rather than opening onto a single entry that scrolls nowhere useful. */
      if (ms.length < 2 && !standalone) { fab.hidden = true; return; }
      fab.hidden = false;
      list.textContent = '';

      ms.forEach(function (m) { list.appendChild(mealItem(m, '')); });
      others.forEach(function (o) { o.render(); });
      spy();
    }

    /* ONE renderer for both pages. `base` is '' for this page — a bare hash
     * that scrolls — and the other page's URL for its menus, which makes those
     * ordinary cross-page links. Everything else is identical, and that is the
     * point: both lists are built by the same code from the same selectors, so
     * neither can drift into a shape the other does not have.
     *
     * data-mv-jump-for is only set for this page's entries. It is what the
     * scroll-spy highlights, and nothing on this page can ever be the current
     * section of another one. */
    function mealItem(m, base) {
      var li = document.createElement('li');
      li.className = 'mv-jump__meal';
      if (!base) li.setAttribute('data-mv-jump-for', m.id);

      var a = document.createElement('a');
      a.href = base + '#' + m.id;
      a.textContent = m.getAttribute('data-mv-meal-name') || m.id;
      li.appendChild(a);

      var cats = [].filter.call(m.querySelectorAll('[data-mv-cat]'), function (c) {
        return !c.hidden && c.id && c.getAttribute('data-mv-cat-name');
      });
      if (cats.length > 1) {
        var ul = document.createElement('ul');
        ul.className = 'mv-jump__cats';
        ul.setAttribute('role', 'list');
        cats.forEach(function (c) {
          var cli = document.createElement('li');
          var ca = document.createElement('a');
          ca.href = base + '#' + c.id;
          ca.textContent = c.getAttribute('data-mv-cat-name');
          cli.appendChild(ca);
          ul.appendChild(cli);
        });
        li.appendChild(ul);
      }
      return li;
    }

    function open(yes) {
      pop.hidden = !yes;
      btn.setAttribute('aria-expanded', yes ? 'true' : 'false');
    }
    /* ⚠️ THE PANEL SURVIVES THE PAGE SWITCH, and it has to be done this way.
     *
     * The This Week / Everyday control inside this panel is two <a href> links
     * to two different PAGES. So the panel was never "closing" when you toggled
     * — the document was unloading and the next one arrived with the panel in
     * its default closed state. Nothing to fix in open(), because open() was
     * never called.
     *
     * The intent is carried across the navigation instead: clicking a switch
     * link inside the panel records that the panel was open, and the next page
     * reopens it and clears the flag. sessionStorage rather than local, so it
     * dies with the tab and cannot reopen the panel days later.
     *
     * Wrapped, because storage throws rather than returning null in private
     * mode on some browsers — and a dead jump control is a worse outcome than
     * a panel that does not reopen. */
    var REOPEN = 'mv-jump-reopen';
    /* ⚠️ EVERY PATH THAT OPENS THE PANEL GOES THROUGH HERE.
     *
     * There are two — the button, and the reopen flag after a cross-page jump —
     * and the fetch has to hang off both. It used to hang off the button alone,
     * which broke the panel in precisely the situation it exists for: follow an
     * "Everyday Menu" link out of the panel, and the panel reopens on the next
     * page having never fetched anything, showing only that page's menus and
     * silently dropping the group the link came from. Closing and reopening did
     * not fix it either, because closing is not opening and the fetch never
     * fired.
     *
     * Opening is what pays for the other page, not page load — and `other.load`
     * is a no-op once it has run, so calling it on every open costs nothing. */
    function openPanel(yes) {
      open(yes);
      if (yes) others.forEach(function (o) { o.load(build); });
    }

    btn.addEventListener('click', function () { openPanel(pop.hidden); });

    list.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a') : null;
      if (!a) return;

      /* A cross-page entry LEAVES, so closing the panel is pointless — the
       * document is about to unload. Record that it was open instead, and the
       * next page reopens it. Same mechanism that used to serve the This Week /
       * Everyday switch before that switch came out of the panel; the trigger
       * moved to the links that replaced it. */
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) !== '#') {
        /* Not from a collection page: there the customer chose a menu, and
         * a panel reopened over it would cover what they asked for. */
        if (!standalone) {
          try { sessionStorage.setItem(REOPEN, '1'); } catch (err) { /* no-op */ }
        }
        return;
      }
      open(false);
    });
    document.addEventListener('click', function (e) {
      if (!fab.contains(e.target)) open(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) { open(false); btn.focus(); }
    });

    /* Which menu is in view. The design's probe: the menu whose top is closest
     * to 140px from the viewport top without being below it. */
    function spy() {
      var ms = meals();
      if (!ms.length) return;
      var probe = 140;
      var best = null;
      ms.forEach(function (m) {
        var t = m.getBoundingClientRect().top;
        if (t <= probe && (!best || t > best.t)) best = { m: m, t: t };
      });
      var id = (best ? best.m : ms[0]).id;
      [].forEach.call(list.querySelectorAll('.mv-jump__meal'), function (li) {
        li.classList.toggle('is-here', li.getAttribute('data-mv-jump-for') === id);
      });
    }

    var raf = 0;
    window.addEventListener('scroll', function () {
      if (!raf) raf = requestAnimationFrame(function () { raf = 0; spy(); });
    }, { passive: true });
    document.addEventListener('maven:menu-filtered', build);

    build();

    /* AFTER build(), and that ordering is the whole of it. The section ships
       the control `hidden` and build() is what reveals it, so a reopen check
       that ran earlier would always see hidden and never fire. */
    try {
      if (sessionStorage.getItem(REOPEN)) {
        sessionStorage.removeItem(REOPEN);
        /* Only if the control is actually usable here. build() hides the whole
         * fab on a page with fewer than two menus, and reopening onto a hidden
         * control would restore a panel nobody can see or dismiss. */
        if (!fab.hidden) openPanel(true);
      }
    } catch (err) { /* no-op */ }
  }

  /* ⚠️ THIS FILE IS LOADED BY TWO SECTIONS AND THEREFORE RUNS TWICE.
   *
   * maven-menu.liquid needs it for the filters and maven-menu-jumpto.liquid
   * needs it for the jump control, and each section has to be self-sufficient
   * — a section cannot know whether another one on the page already asked for
   * the file. Two <script src> tags for one URL is ONE fetch but TWO
   * executions; the browser caches the file, it does not skip the element.
   *
   * Unguarded, that appended the six diet pills twice — the trial store showed
   * twelve — and left two independent filter states writing over each other on
   * the same dish list, so a pill from one set was silently undone by the
   * other. Found on the rendered page on 2026-08-30, not in review.
   *
   * The guard is per ELEMENT, not a module-level "already booted" flag,
   * because the theme editor re-inserts a section and fires
   * shopify:section:load: a fresh element has no flag and initialises, which
   * is what should happen, while a module flag would leave it dead. */
  /* "How to read the labels" (snippets/maven-label-toggle.liquid). The note
   * renders open, so it is readable without this script. Here it closes, and
   * the button opens and closes it. */
  function initHowto(root) {
    var btn = root.querySelector('[data-mv-howto]');
    if (!btn) return;
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    panel.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });
  }

  /* A DISH CARD'S DETAILS, 2026-10-02 (snippets/maven-dish-card.liquid).
   *
   * Each card has a "Details" button beside its diet pills, and a hidden
   * panel under them. One click handler on the document serves every card,
   * including cards that the search adds later from the other menu page.
   *
   * ⚠️ THIS FILE CAN LOAD TWICE ON ONE PAGE. sections/maven-menu.liquid and
   * sections/maven-menu-jumpto.liquid both include it. A second handler would
   * toggle each panel twice, which looks like a dead button. So the handler is
   * bound once, and the <html> attribute records it. */
  function setPanel(btn, open) {
    var card = btn.closest('.mv-dish');
    var panel = card && card.querySelector('[data-mv-details-panel]');
    if (!panel) return;
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  if (!document.documentElement.hasAttribute('data-mv-details-bound')) {
    document.documentElement.setAttribute('data-mv-details-bound', '1');
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-mv-details-toggle]');
      if (!btn) return;
      setPanel(btn, btn.getAttribute('aria-expanded') !== 'true');
    });
  }

  /* "Show ALL Product Details", next to Match All / Any (Heidi, 2026-10-02).
   * It opens or closes the details of every card on the page. It replaced a
   * toggle in each meal's heading the same day.
   *
   * The choice is remembered in localStorage, as a convenience for one
   * viewer. Every read and write is in try/catch: a private window can refuse
   * storage, and the page must work the same without it. */
  var DETAILS_KEY = 'mv-menu-details-all';
  function initDetailsAll(root) {
    var pill = root.querySelector('[data-mv-details-all]');
    if (!pill) return;
    if (!document.querySelector('[data-mv-details-toggle]')) return;
    function set(open, remember) {
      [].forEach.call(document.querySelectorAll('[data-mv-details-toggle]'), function (btn) {
        setPanel(btn, open);
      });
      pill.setAttribute('aria-pressed', open ? 'true' : 'false');
      pill.querySelector('.mv-menu-filters__details-long').textContent =
        pill.getAttribute(open ? 'data-label-hide' : 'data-label-show');
      pill.querySelector('.mv-menu-filters__details-short').textContent =
        pill.getAttribute(open ? 'data-label-hide-short' : 'data-label-show-short');
      if (!remember) return;
      try {
        if (open) localStorage.setItem(DETAILS_KEY, '1');
        else localStorage.removeItem(DETAILS_KEY);
      } catch (err) { /* no-op */ }
    }
    var saved = false;
    try { saved = localStorage.getItem(DETAILS_KEY) === '1'; } catch (err) { /* no-op */ }
    if (saved) set(true, false);
    pill.hidden = false;
    pill.addEventListener('click', function () {
      set(pill.getAttribute('aria-pressed') !== 'true', true);
    });
    /* The search adds cards from the other menu page later (renderAlso).
     * They arrive closed, so while the pill is on, open them too. Otherwise
     * the pill says "Hide ALL" over cards that it never opened. */
    document.addEventListener('maven:dishes-added', function (e) {
      if (pill.getAttribute('aria-pressed') !== 'true') return;
      var cards = (e.detail && e.detail.cards) || [];
      cards.forEach(function (card) {
        var btn = card.querySelector('[data-mv-details-toggle]');
        if (btn) setPanel(btn, true);
      });
    });
  }

  function boot() {
    [].forEach.call(document.querySelectorAll('.shopify-section'), function (s) {
      if (!s.querySelector('[data-mv-filters]')) return;
      if (s.getAttribute('data-mv-filters-ready')) return;
      s.setAttribute('data-mv-filters-ready', '1');
      init(s);
      initHowto(s);
      initDetailsAll(s);
    });
    initJump();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
